import type { AutomationConfig } from '../services/automation';

interface DiscoveredDatabaseReference {
  blockId: string;
  docId?: string | null;
}

type DatabaseReferenceMoveUpdates = Pick<
  AutomationConfig,
  'sourceDocId' | 'targetDocId' | 'inputTables' | 'outputTables'
>;

/**
 * Reconcile database references only when discovery positively identifies a
 * referenced database in another doc. Missing discovery entries are not a
 * deletion signal; they can be transient during reload, sync, or execution UI
 * refreshes.
 */
export function computeDatabaseReferenceMoveUpdates(
  config: AutomationConfig,
  databases: readonly DiscoveredDatabaseReference[],
  ownerDocId?: string | null
): Partial<DatabaseReferenceMoveUpdates> | null {
  const updates: Partial<DatabaseReferenceMoveUpdates> = {};

  const sourceDb = config.sourceTableId
    ? databases.find(db => db.blockId === config.sourceTableId)
    : null;
  if (sourceDb?.docId) {
    const desiredSourceDocId =
      ownerDocId && sourceDb.docId === ownerDocId ? null : sourceDb.docId;
    if ((config.sourceDocId ?? null) !== desiredSourceDocId) {
      updates.sourceDocId = desiredSourceDocId;
    }
  }

  const legacyTargetDb = config.targetTableId
    ? databases.find(db => db.blockId === config.targetTableId)
    : null;
  if (legacyTargetDb?.docId) {
    const desiredTargetDocId =
      ownerDocId && legacyTargetDb.docId === ownerDocId
        ? null
        : legacyTargetDb.docId;
    if ((config.targetDocId ?? null) !== desiredTargetDocId) {
      updates.targetDocId = desiredTargetDocId;
    }
  }

  const inputTables = config.inputTables ?? [];
  if (inputTables.length > 0) {
    let inputMoved = false;
    const movedInputTables = inputTables.map(inputTable => {
      if (!inputTable.tableId) return inputTable;

      const targetDb = databases.find(db => db.blockId === inputTable.tableId);
      if (!targetDb?.docId) return inputTable;

      const desiredDocId =
        ownerDocId && targetDb.docId === ownerDocId ? null : targetDb.docId;
      if ((inputTable.docId ?? null) === desiredDocId) return inputTable;

      inputMoved = true;
      return {
        ...inputTable,
        docId: desiredDocId ?? undefined,
      };
    });

    if (inputMoved) {
      updates.inputTables = movedInputTables;
    }
  }

  const outputTables = config.outputTables ?? [];
  if (outputTables.length > 0) {
    let outputMoved = false;
    const movedOutputTables = outputTables.map(outputTable => {
      if (!outputTable.tableId) return outputTable;

      const targetDb = databases.find(db => db.blockId === outputTable.tableId);
      if (targetDb?.docId && (outputTable.docId ?? null) !== targetDb.docId) {
        outputMoved = true;
        return { ...outputTable, docId: targetDb.docId };
      }

      return outputTable;
    });

    if (outputMoved) {
      updates.outputTables = movedOutputTables;
    }
  }

  return Object.keys(updates).length > 0 ? updates : null;
}
